// version a tester 2
import React, { useState, useEffect, useRef } from 'react';
import OT from '@opentok/client';
import { Capacitor } from '@capacitor/core';

import './VideoCall.css';

const isNativeApp = Capacitor.isNativePlatform();

// Composant pour une vidéo individuelle
const VideoTile = ({ 
  publisher = null, 
  subscriber = null, 
  isLocal = false,
  className = "",
  showControls = false 
}) => {
  const containerRef = useRef(null);
  const [hasVideo, setHasVideo] = useState(true);
  const [hasAudio, setHasAudio] = useState(true);
  const [isAttached, setIsAttached] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    let mediaElement = null;

    if (isLocal && publisher && !isAttached) {
      console.log("Attachement vidéo locale...");
      try {
        if (publisher.element) {
          container.innerHTML = '';
          container.appendChild(publisher.element);
          mediaElement = publisher;
          setIsAttached(true);
          
          setHasVideo(publisher.getVideoSource() !== null);
          setHasAudio(publisher.getAudioSource() !== null);
          
          publisher.on('videoEnabled', () => setHasVideo(true));
          publisher.on('videoDisabled', () => setHasVideo(false));
          publisher.on('audioEnabled', () => setHasAudio(true));  
          publisher.on('audioDisabled', () => setHasAudio(false));
        }
      } catch (error) {
        console.error("Erreur attachement publisher:", error);
      }
    }
    else if (!isLocal && subscriber && !isAttached) {
      console.log("Attachement vidéo distante...");
      try {
        if (subscriber.element) {
          container.innerHTML = '';
          container.appendChild(subscriber.element);
          mediaElement = subscriber;
          setIsAttached(true);
          
          setHasVideo(subscriber.hasVideo());
          setHasAudio(subscriber.hasAudio());
          
          subscriber.on('videoEnabled', () => setHasVideo(true));
          subscriber.on('videoDisabled', () => setHasVideo(false));
          subscriber.on('audioEnabled', () => setHasAudio(true));
          subscriber.on('audioDisabled', () => setHasAudio(false));
        }
      } catch (error) {
        console.error("Erreur attachement subscriber:", error);
      }
    }

    // ✅ CORRECTION CRITIQUE : Nettoyage complet des tracks média
    return () => {
      if (isAttached && mediaElement) {
        try {
          console.log(`=== CLEANUP VideoTile (${isLocal ? 'local' : 'remote'}) ===`);
          
          // 1. Retirer les event listeners
          if (isLocal) {
            mediaElement.off('videoEnabled');
            mediaElement.off('videoDisabled');
            mediaElement.off('audioEnabled');
            mediaElement.off('audioDisabled');
          } else {
            mediaElement.off('videoEnabled');
            mediaElement.off('videoDisabled');
            mediaElement.off('audioEnabled');
            mediaElement.off('audioDisabled');
          }
          
          // ✅ 2. NOUVEAU : Arrêter tous les tracks des éléments <video> dans le container
          const videoElements = container.querySelectorAll('video');
          videoElements.forEach(videoEl => {
            try {
              if (videoEl.srcObject) {
                const tracks = videoEl.srcObject.getTracks();
                tracks.forEach(track => {
                  track.stop();
                  console.log(`✓ VideoTile: Track ${track.kind} arrêté (${isLocal ? 'local' : 'remote'})`);
                });
                videoEl.srcObject = null;
              }
            } catch (error) {
              console.log("Erreur arrêt track VideoTile:", error);
            }
          });
          
          // 3. Vider le container
          container.innerHTML = '';
          
          console.log(`✓ VideoTile cleanup terminé (${isLocal ? 'local' : 'remote'})`);
          
        } catch (error) {
          console.log("Erreur nettoyage VideoTile:", error);
        }
        setIsAttached(false);
      }
    };
  }, [publisher, subscriber, isLocal, isAttached]);

  return (
    <div className={`video-container ${className}`}>
      <div 
        ref={containerRef}
        className={isLocal ? "local-video" : "remote-video"}
      />
      
      {!hasVideo && (
        <div className="video-disabled-overlay">
          <div className="icon">📷</div>
          <div className="text">Caméra désactivée</div>
        </div>
      )}
      
      {!hasAudio && !isLocal && (
        <div className="audio-muted-indicator">🔇</div>
      )}
      
      {isLocal && (
        <div className="local-label">Vous</div>
      )}
    </div>
  );
};

// Composant principal
export default function VideoCall({ 
  session, 
  publisher, 
  subscribers = [],
  vonageConfig,
  onLeave 
}) {
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [screenSharePublisher, setScreenSharePublisher] = useState(null);
  const [error, setError] = useState(null);

  const regularSubscribers = subscribers.filter(sub => !sub.isScreenShare);
  const screenShareSubscribers = subscribers.filter(sub => sub.isScreenShare);
  const mainRemoteSubscriber = regularSubscribers[0] || null;

  // Initialisation correcte des états au chargement du publisher
  useEffect(() => {
    if (publisher) {
      try {
        if (isNativeApp) {
          const videoTrack = publisher.getVideoSource();
          const audioTrack = publisher.getAudioSource();
          
          if (videoTrack && videoTrack.enabled !== undefined) {
            setIsVideoMuted(!videoTrack.enabled);
            console.log("État initial vidéo:", videoTrack.enabled ? "ACTIVÉE" : "DÉSACTIVÉE");
          }
          
          if (audioTrack && audioTrack.enabled !== undefined) {
            setIsAudioMuted(!audioTrack.enabled);
            console.log("État initial audio:", audioTrack.enabled ? "ACTIVÉ" : "DÉSACTIVÉ");
          }
        } else {
          setIsVideoMuted(false);
          setIsAudioMuted(false);
          console.log("Publisher initialisé - vidéo et audio actifs (web)");
        }
      } catch (error) {
        console.error("Erreur sync états initiaux:", error);
        setIsVideoMuted(false);
        setIsAudioMuted(false);
      }
    }
  }, [publisher]);

  // ✅ AMÉLIORATION : Cleanup au unmount du composant VideoCall
  useEffect(() => {
    return () => {
      console.log("=== CLEANUP VideoCall UNMOUNT ===");
      
      // Nettoyer le screen share publisher si actif
      if (screenSharePublisher && session) {
        try {
          const stream = screenSharePublisher.stream?.getMediaStream?.();
          if (stream && stream.getTracks) {
            stream.getTracks().forEach(track => {
              track.stop();
              console.log(`✓ ScreenShare track ${track.kind} arrêté au unmount`);
            });
          }
          
          session.unpublish(screenSharePublisher);
          screenSharePublisher.destroy();
          console.log("✓ ScreenShare publisher nettoyé au unmount");
        } catch (error) {
          console.error("Erreur cleanup screenshare au unmount:", error);
        }
      }
      
      // ✅ NOUVEAU : Nettoyer TOUS les éléments <video> résiduels
      const allVideoElements = document.querySelectorAll('.video-call-container video');
      allVideoElements.forEach(videoEl => {
        try {
          if (videoEl.srcObject) {
            const tracks = videoEl.srcObject.getTracks();
            tracks.forEach(track => {
              track.stop();
              console.log(`✓ Track ${track.kind} arrêté depuis DOM au unmount`);
            });
            videoEl.srcObject = null;
          }
        } catch (error) {
          console.log("Erreur nettoyage video DOM au unmount:", error);
        }
      });
      
      console.log("✓ VideoCall cleanup complet terminé");
    };
  }, [screenSharePublisher, session]);

  // Toggle vidéo corrigé
  const toggleVideo = () => {
    if (!publisher) {
      console.error("Pas de publisher disponible");
      return;
    }
    
    console.log("Toggle vidéo - État actuel isVideoMuted:", isVideoMuted);
    
    try {
      if (isNativeApp) {
        const videoTrack = publisher.getVideoSource();
        
        if (videoTrack && videoTrack.enabled !== undefined) {
          const currentTrackState = videoTrack.enabled;
          const newTrackState = !currentTrackState;
          
          videoTrack.enabled = newTrackState;
          setIsVideoMuted(!newTrackState);
          
          console.log("→ Track vidéo était:", currentTrackState, "maintenant:", newTrackState);
          console.log("→ isVideoMuted mis à jour:", !newTrackState);
        } else {
          const newState = !isVideoMuted;
          publisher.publishVideo(!newState);
          setIsVideoMuted(newState);
          console.log("→ Vidéo (fallback publishVideo):", !newState ? "ACTIVÉE" : "DÉSACTIVÉE");
        }
      } else {
        const newState = !isVideoMuted;
        publisher.publishVideo(!newState);
        setIsVideoMuted(newState);
        console.log("→ Vidéo web:", !newState ? "ACTIVÉE" : "DÉSACTIVÉE");
      }
    } catch (error) {
      console.error("Erreur toggle vidéo:", error);
    }
  };

  // Toggle audio corrigé
  const toggleAudio = () => {
    if (!publisher) {
      console.error("Pas de publisher disponible");
      return;
    }
    
    console.log("Toggle audio - État actuel isAudioMuted:", isAudioMuted);
    
    try {
      if (isNativeApp) {
        const audioTrack = publisher.getAudioSource();
        
        if (audioTrack && audioTrack.enabled !== undefined) {
          const currentTrackState = audioTrack.enabled;
          const newTrackState = !currentTrackState;
          
          audioTrack.enabled = newTrackState;
          setIsAudioMuted(!newTrackState);
          
          console.log("→ Track audio était:", currentTrackState, "maintenant:", newTrackState);
          console.log("→ isAudioMuted mis à jour:", !newTrackState);
        } else {
          const newState = !isAudioMuted;
          publisher.publishAudio(!newState);
          setIsAudioMuted(newState);
          console.log("→ Audio (fallback publishAudio):", !newState ? "ACTIVÉ" : "DÉSACTIVÉ");
        }
      } else {
        const newState = !isAudioMuted;
        publisher.publishAudio(!newState);
        setIsAudioMuted(newState);
        console.log("→ Audio web:", !newState ? "ACTIVÉ" : "DÉSACTIVÉ");
      }
    } catch (error) {
      console.error("Erreur toggle audio:", error);
    }
  };

  const toggleScreenShare = async () => {
    if (!session) return;

    try {
      if (isScreenSharing && screenSharePublisher) {
        // Arrêter le partage d'écran
        const stream = screenSharePublisher.stream?.getMediaStream?.();
        if (stream && stream.getTracks) {
          stream.getTracks().forEach(track => {
            track.stop();
            console.log(`✓ ScreenShare track ${track.kind} arrêté`);
          });
        }
        
        session.unpublish(screenSharePublisher);
        screenSharePublisher.destroy();
        setScreenSharePublisher(null);
        setIsScreenSharing(false);
        console.log("✓ Partage d'écran arrêté");
      } else {
        // Démarrer le partage d'écran
        const screenPublisher = OT.initPublisher(null, {
          videoSource: 'screen',
          publishAudio: true,
          publishVideo: true,
          name: 'Screen Share'
        }, (error) => {
          if (error) {
            console.error("Erreur screen share:", error);
            setError("Impossible de partager l'écran: " + error.message);
          } else {
            session.publish(screenPublisher, (publishError) => {
              if (publishError) {
                console.error("Erreur publication:", publishError);
                screenPublisher.destroy();
                setError("Erreur de publication du partage d'écran");
              } else {
                setScreenSharePublisher(screenPublisher);
                setIsScreenSharing(true);
                console.log("✓ Partage d'écran démarré");
              }
            });
          }
        });
      }
    } catch (error) {
      console.error("Erreur toggle screen share:", error);
      setError("Erreur lors du partage d'écran");
    }
  };

  // ✅ VERSION AMÉLIORÉE avec cleanup complet
  const handleLeave = async () => {
    if (window.confirm("Êtes-vous sûr de vouloir quitter ?")) {
      console.log("=== DÉBUT LEAVE DEPUIS VideoCall ===");
      
      // Arrêter le screen share si actif
      if (screenSharePublisher && session) {
        try {
          const stream = screenSharePublisher.stream?.getMediaStream?.();
          if (stream && stream.getTracks) {
            stream.getTracks().forEach(track => {
              track.stop();
              console.log(`✓ ScreenShare track ${track.kind} arrêté avant leave`);
            });
          }
          session.unpublish(screenSharePublisher);
          screenSharePublisher.destroy();
          setScreenSharePublisher(null);
          setIsScreenSharing(false);
          console.log("✓ ScreenShare arrêté avant leave");
        } catch (error) {
          console.error("Erreur arrêt screenshare:", error);
        }
      }
      
      // Petit délai pour assurer le cleanup
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Appeler la fonction de leave parent
      console.log("Appel onLeave() parent...");
      onLeave();
    }
  };

  const clearError = () => setError(null);

  if (error) {
    return (
      <div className="video-call-container">
        <div className="error-container">
          <h2>Erreur</h2>
          <p>{error}</p>
          <button onClick={clearError}>OK</button>
        </div>
      </div>
    );
  }

  return (
    <div className="video-call-container">
      
      <div className="main-video-area">
        {mainRemoteSubscriber ? (
          <VideoTile 
            subscriber={mainRemoteSubscriber.subscriber}
            isLocal={false}
            className="remote-video-container"
          />
        ) : (
          <div className="waiting-message">
            <h2>En attente de connexion</h2>
            <p>Votre consultant va bientôt vous rejoindre...</p>
            <div className="spinner"></div>
          </div>
        )}
      </div>

      {screenShareSubscribers.length > 0 && (
        <div className="screenshare-container">
          <VideoTile 
            subscriber={screenShareSubscribers[0].subscriber}
            isLocal={false}
            className="screenshare-video"
          />
        </div>
      )}

      {publisher && (
        <div className="local-video-pip">
          <VideoTile 
            publisher={publisher}
            isLocal={true}
            className="local-video"
          />
        </div>
      )}

      <div className="controls-bar">
        <div className="controls-group">
          
          <button 
            className={`control-button ${isVideoMuted ? 'muted' : ''}`}
            onClick={toggleVideo}
            title={isVideoMuted ? 'Activer la caméra' : 'Désactiver la caméra'}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isVideoMuted ? (
                <>
                  <path d="M16 16v1a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10"/>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                </>
              ) : (
                <>
                  <polygon points="23 7 16 12 23 17 23 7"/>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                </>
              )}
            </svg>
          </button>

          <button 
            className={`control-button ${isAudioMuted ? 'muted' : ''}`}
            onClick={toggleAudio}
            title={isAudioMuted ? 'Activer le micro' : 'Couper le micro'}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isAudioMuted ? (
                <>
                  <line x1="1" y1="1" x2="23" y2="23"/>
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
                  <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </>
              ) : (
                <>
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </>
              )}
            </svg>
          </button>

          <button 
            className={`control-button ${isScreenSharing ? 'active' : ''}`}
            onClick={toggleScreenShare}
            title={isScreenSharing ? 'Arrêter le partage' : 'Partager l\'écran'}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
              <line x1="8" y1="21" x2="16" y2="21"/>
              <line x1="12" y1="17" x2="12" y2="21"/>
            </svg>
          </button>

          <button 
            className="control-button danger"
            onClick={handleLeave}
            title="Quitter l'appel"
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>

        </div>
      </div>
    </div>
  );
}